// Path: apps/0001-estata.com/frontend/src/lib/invitation.ts
// v1.0.0 - Die Tuer fuer Einladungslinks (Inhaber, 12.10.2026: der Mitarbeiter "muss doch auf der Anmeldungsseite
//          sein Passwort eingeben"): EIN Link-Format (/login?invite=<token>), EIN Fenster, zwei Herkuenfte. Ein
//          Projektbeteiligter kommt aus Planstar (guests/public/invite), ein Mitarbeiter aus dem System-Backend
//          (invitations/info, Onboarding aus MyEST). Das Fenster fragt nie, woher - es sieht nur InviteInfo.

import axios from 'axios';

export type InviteKind = 'party' | 'employee';

export interface InviteInfo {
  email: string;
  person_name: string | null;
  has_account: boolean;
  multi_use: boolean;
  password_min_length: number;
  known_names: string[];
  /** E-5: hat das Konto mehrere Firmen, die Auswahl - sonst leer */
  companies?: { id: string; name: string }[];
  /** Woher die Einladung kommt - nur die Tuer unterscheidet danach */
  kind: InviteKind;
  /** Mitarbeiter: Person schon angelegt (Onboarding) -> nur Passwort; sonst Name + Passwort */
  pre_linked?: boolean;
}

const PASSWORD_MIN_LENGTH = 8;
const party = (token: string) => `/planstar/api/v1/guests/public/invite/${encodeURIComponent(token)}`;

/** Was hinter dem Link steckt. Erst Planstar (Projektbeteiligte), bei 'gab es nie' das System (Mitarbeiter);
 *  kennt es keiner, gilt die Antwort von Planstar (404 nie, 410 vorbei). */
export async function lookupInvitation(token: string): Promise<InviteInfo> {
  try {
    const res = await axios.get(party(token));
    // Erst JETZT "gesehen" melden - als POST, denn Virenscanner rufen Links ab, sie schicken keine POSTs.
    axios.post(`${party(token)}/opened`).catch(() => {});
    return { ...res.data, kind: 'party' };
  } catch (err: any) {
    if (err?.response?.status !== 404) throw err;
    let res;
    try {
      res = await axios.get('/system/api/v1/invitations/info', { params: { token } });
    } catch {
      throw err;
    }
    const d = res.data || {};
    const name = [d.first_name, d.last_name].filter(Boolean).join(' ').trim() || null;
    return {
      email: d.email,
      person_name: name,
      has_account: false,
      multi_use: !d.is_pre_linked,
      password_min_length: PASSWORD_MIN_LENGTH,
      known_names: [],
      companies: [],
      kind: 'employee',
      pre_linked: !!d.is_pre_linked,
    };
  }
}

export interface SetupPayload {
  password: string;
  display_name?: string;
  org_id?: string;
  device_label: string;
  client_timezone: string;
  language: string;
}

/** Zugang einrichten - danach meldet sich das Fenster mit E-Mail + Passwort am System an, fuer beide gleich. */
export async function setUpInvitation(token: string, info: InviteInfo, p: SetupPayload): Promise<void> {
  if (info.kind === 'party') {
    await axios.post(`${party(token)}/setup`, {
      password: p.password, display_name: p.display_name, org_id: p.org_id,
      device_label: p.device_label, client_timezone: p.client_timezone,
    }, { withCredentials: true });
    return;
  }
  if (info.pre_linked) {
    await axios.post('/system/api/v1/invitations/activate', {
      token, password: p.password, preferred_language: (p.language || 'de').slice(0, 2),
    });
    return;
  }
  const [first, ...rest] = (p.display_name || '').trim().split(/\s+/);
  await axios.post('/system/api/v1/invitations/accept', {
    token, password: p.password, first_name: first || null, last_name: rest.join(' ') || null,
  });
}

/** E-6: ablehnen gibt es nur fuer Projektbeteiligte (Nachricht an den Einladenden). */
export const canDecline = (info: InviteInfo | null): boolean => !!info && info.kind === 'party';
export const declineInvitation = (token: string) => axios.post(`${party(token)}/decline`);
